/**
 * Everything the tab's layout reads, computed in one hook: the URL round
 * and entry switches, the tiles, a live lock countdown, and the view
 * builders' outputs (S1 to S7).
 */
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";

import { buildPickTiles } from "../pick/pickBoardModel";
import { openResultsContestSearch } from "../poolStandings/poolStandingsRoute";
import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import { buildMyPickCards, type typeMyPickCard } from "./survivorCardModel";
import type { typeSurvivorStatusBarProps } from "./SurvivorStatusBar";
import {
  buildGameStateLabels,
  buildMatchCardViews,
  buildSurvivorStatusBar,
  type typeMatchCardView,
} from "./survivorTabView";
import { useAddEntry } from "./useAddEntry";
import {
  type typeSurvivorGameData,
  useSurvivorGameData,
} from "./useSurvivorGameData";
import {
  type typeSurvivorPickSelection,
  useSurvivorPickSelection,
} from "./useSurvivorPickSelection";

export type typeSurvivorAddEntryAction = {
  isPending: boolean;
  onPress: () => void;
};

export type typeSurvivorGameTabModel = {
  data: typeSurvivorGameData;
  selection: typeSurvivorPickSelection;
  matchCards: typeMatchCardView[];
  myPickCards: typeMyPickCard[];
  showPriority: boolean;
  isReadOnly: boolean;
  statusBar: typeSurvivorStatusBarProps | null;
  /** Null when the Add another entry button is not rendered (ISSUE-1043). */
  addEntryAction: typeSurvivorAddEntryAction | null;
  selectRound: (roundId: string) => Promise<void>;
  selectEntry: (entryId: string) => Promise<void>;
  /** ISSUE-1331. Switches the board to another contest of the season. */
  selectContest: (contestId: string) => Promise<void>;
};

export function useSurvivorGameTab(input: {
  poolId: string;
  contestId?: string | undefined;
}): typeSurvivorGameTabModel {
  const search = useSearch({ strict: false });
  const data = useSurvivorGameData({
    poolId: input.poolId,
    requestedRoundId:
      typeof search.roundId === "string" ? search.roundId : undefined,
    requestedEntryId:
      typeof search.entryId === "string" ? search.entryId : undefined,
    allowUpcomingSelection: input.contestId !== undefined,
    requestedContestId:
      input.contestId ??
      (typeof search.contestId === "string" ? search.contestId : undefined),
  });

  const navigate = useNavigate();

  const isSpreadContest = data.contestData?.config.pickBasis === "spread";

  const tilesForSelection = useMemo(
    () =>
      buildPickTiles({
        board: data.boardData,
        teamsById: data.teamsById,
        selections: [],
        isSpreadContest,
      }),
    [data.boardData, data.teamsById, isSpreadContest],
  );

  const selection = useSurvivorPickSelection({
    poolId: input.poolId,
    contestId: data.contestId,
    roundId: data.selectedRoundId,
    entryId: data.selectedEntryId,
    boardData: data.boardData,
    view: data.view,
    tiles: tilesForSelection,
  });

  const addEntry = useAddEntry({
    poolId: input.poolId,
    contestId: data.contestId,
    confirmDiscardIfDirty: selection.confirmDiscardIfDirty,
    onCreated: goToEntry,
  });

  const tiles = useMemo(
    () =>
      buildPickTiles({
        board: data.boardData,
        teamsById: data.teamsById,
        selections: selection.selections,
        isSpreadContest,
      }),
    [data.boardData, data.teamsById, selection.selections, isSpreadContest],
  );

  const [nowMs, setNowMs] = useState(() => Date.now());
  const refetchedForLocksAtRef = useRef<string | null>(null);
  useEffect(() => {
    const timer = setInterval(() => {
      setNowMs(Date.now());
    }, 30_000);
    return () => {
      clearInterval(timer);
    };
  }, []);
  useEffect(() => {
    const boardData = data.boardData;
    if (
      boardData !== null &&
      boardData.locksAt !== null &&
      !boardData.locked &&
      nowMs >= Date.parse(boardData.locksAt) &&
      refetchedForLocksAtRef.current !== boardData.locksAt
    ) {
      refetchedForLocksAtRef.current = boardData.locksAt;
      data.refetchBoard();
    }
  }, [data.boardData, nowMs]);

  async function selectRound(roundId: string): Promise<void> {
    if (roundId === data.selectedRoundId) return;
    if (!(await selection.confirmDiscardIfDirty())) return;
    void navigate({
      to: ".",
      search: (previous: unknown) => ({
        ...(previous as Record<string, unknown>),
        roundId,
      }),
      replace: true,
    });
  }

  function goToEntry(entryId: string): void {
    void navigate({
      to: ".",
      search: (previous: unknown) => ({
        ...(previous as Record<string, unknown>),
        entryId,
      }),
    });
  }

  async function selectEntry(entryId: string): Promise<void> {
    if (entryId === data.selectedEntryId) return;
    if (!(await selection.confirmDiscardIfDirty())) return;
    goToEntry(entryId);
  }

  // ISSUE-1331. The same `?contestId=` the Results tab's menu writes, and
  // the same drop of roundId and entryId, which belonged to the old contest.
  async function selectContest(contestId: string): Promise<void> {
    if (contestId === data.contestId) return;
    if (!(await selection.confirmDiscardIfDirty())) return;
    void navigate({
      to: ".",
      search: (previous: unknown) =>
        openResultsContestSearch(
          (previous ?? {}) as Record<string, unknown>,
          contestId,
        ),
      replace: true,
    });
  }

  const requiredPickCount = data.boardData?.requiredPickCount ?? 0;
  const showPriority = requiredPickCount > 1;
  const isReadOnly = data.view.kind !== "open";

  const addEntryAction: typeSurvivorAddEntryAction | null = data.canAddEntry
    ? {
        isPending: addEntry.isPending,
        onPress: () => {
          void addEntry.addEntry();
        },
      }
    : null;

  const gameStateLabelByEventId = useMemo(
    () =>
      buildGameStateLabels({
        boardData: data.boardData,
        timeZone: resolveViewerTimeZone(),
        nowMs,
      }),
    [data.boardData, nowMs],
  );

  const matchCards = buildMatchCardViews({
    tiles,
    selections: selection.selections,
    savedPicks: data.boardData?.myPicks ?? [],
    gameStateLabelByEventId,
    teamsById: data.teamsById,
    showPriority,
  });

  const myPickCards = buildMyPickCards({
    selections: selection.selections,
    tiles,
    savedPicks: data.boardData?.myPicks ?? [],
    gameStateLabelByEventId,
    teamsById: data.teamsById,
  });

  const statusBar = buildSurvivorStatusBar({
    viewKind: data.view.kind,
    hasEntry: data.hasEntry,
    entryStatus: data.entryStatus,
    boardData: data.boardData,
    nowMs,
    primaryAction: selection.primaryAction,
  });

  return {
    data,
    selection,
    matchCards,
    myPickCards,
    showPriority,
    isReadOnly,
    statusBar,
    addEntryAction,
    selectRound,
    selectEntry,
    selectContest,
  };
}
